import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Modal, Input, Typography, message, Select } from 'antd'
import { apiUrl } from '../config/api'
import { fetchDeliveryLocations } from '../api/deliveryLocations'

function getDeliveryLocationId() {
  try {
    const raw = localStorage.getItem('deliveryLocation')
    if (!raw) return null
    const obj = JSON.parse(raw)
    return obj?.id || null
  } catch { return null }
}

function validatePhone(p) {
  return /^05\d{8}$/.test(String(p || '').trim())
}

// Imperative OtpAuthFlow that can be opened from anywhere
// Usage: const ref = useRef(); <OtpAuthFlow ref={ref} onSuccess={...} />; ref.current.open()
export default forwardRef(function OtpAuthFlow({ onSuccess }, ref) {
  const [visible, setVisible] = useState(false)
  const [stage, setStage] = useState('phone') // 'phone' | 'verify'
  const [loading, setLoading] = useState(false)
  const [userExists, setUserExists] = useState(false)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')

  // Delivery locations state for new users
  const [locations, setLocations] = useState([])
  const [locLoading, setLocLoading] = useState(false)
  const [deliveryLocationId, setDeliveryLocationId] = useState(getDeliveryLocationId())

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true)
      setStage('phone')
      setLoading(false)
      setUserExists(false)
      setPhone('')
      setCode('')
      setName('')
      setAddress('')
      // reset locations state and prefill deliveryLocationId from storage
      setLocations([])
      setLocLoading(false)
      setDeliveryLocationId(getDeliveryLocationId())
    },
    close: () => setVisible(false),
  }))

  async function sendOtp() {
    try {
      const p = String(phone || '').trim()
      if (!validatePhone(p)) {
        message.error('رقم الجوال غير صالح. يجب أن يبدأ بـ 05 ويتكون من 10 أرقام.')
        return
      }
      setLoading(true)
      const resp = await fetch(apiUrl('/api/users/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: p }),
      })
      const json = await resp.json().catch(() => null)
      if (!resp.ok) {
        message.error(json?.otpSendingResult || 'فشل إرسال رمز التحقق')
        return
      }
      const exists = !!json?.userExists
      setUserExists(exists)
      message.success('تم إرسال رمز التحقق')
      setStage('verify')
      // If new user, load delivery locations for selection
      if (!exists) {
        try {
          setLocLoading(true)
          const list = await fetchDeliveryLocations().catch(() => [])
          setLocations(Array.isArray(list) ? list : [])
          // keep any pre-selected id from localStorage if present
          if (!getDeliveryLocationId() && list && list.length > 0) {
            setDeliveryLocationId(list[0]?.id ?? null)
          }
        } finally {
          setLocLoading(false)
        }
      }
    } catch (e) {
      console.error(e)
      message.error('فشل إرسال رمز التحقق')
    } finally {
      setLoading(false)
    }
  }

  async function completeOtp() {
    try {
      const p = String(phone || '').trim()
      if (!validatePhone(p)) { message.error('رقم الجوال غير صالح'); return }
      if (!code || String(code).trim().length < 4) { message.error('الرجاء إدخال رمز صحيح'); return }

      const storedLocId = getDeliveryLocationId()
      const selectedLocId = deliveryLocationId || storedLocId
      const payload = { phone: p, code: String(code).trim() }
      if (!userExists) {
        if (!name || !address || !selectedLocId) {
          message.error('الاسم والعنوان وموقع التوصيل مطلوبة للمستخدم الجديد')
          return
        }
        payload.name = name.trim()
        payload.deliveryLocationId = String(selectedLocId)
        payload.fullAddress = address.trim()
      }

      setLoading(true)
      const resp = await fetch(apiUrl('/api/users/complete-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await resp.json().catch(() => null)
      if (!resp.ok || !json?.success) {
        const msgKey = json?.message
        if (msgKey === 'user_inactive') {
          message.error(`الحساب معطل: ${json?.deactivationReason || ''}`)
        } else {
          message.error('فشل التحقق من الرمز')
        }
        return
      }

      // Store tokens and user object
      try {
        if (json.accessToken) localStorage.setItem('accessToken', json.accessToken)
        if (json.refreshToken) localStorage.setItem('refreshToken', json.refreshToken)
        if (json.user) localStorage.setItem('user', JSON.stringify(json.user))
        // Persist delivery location selection if available
        if (selectedLocId) {
          const locObj = (locations || []).find(l => l.id === selectedLocId) || null
          if (locObj) localStorage.setItem('deliveryLocation', JSON.stringify(locObj))
        }
      } catch { /* ignore */ }

      setVisible(false)
      onSuccess?.()
    } catch (e) {
      console.error(e)
      message.error('تعذر إكمال التحقق')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Phone modal */}
      <Modal
        open={visible && stage === 'phone'}
        title="تسجيل الدخول"
        okText="إرسال رمز التحقق"
        cancelText="إلغاء"
        confirmLoading={loading}
        onCancel={() => setVisible(false)}
        onOk={sendOtp}
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <Typography.Paragraph>الرجاء إدخال رقم الجوال لإرسال رمز التحقق.</Typography.Paragraph>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="مثال: 0595566789"
            maxLength={10}
          />
          <Typography.Text type="secondary">يجب أن يبدأ بـ 05 ويتكون من 10 أرقام.</Typography.Text>
        </div>
      </Modal>

      {/* Verify modal */}
      <Modal
        open={visible && stage === 'verify'}
        title="تأكيد رمز التحقق"
        okText="تأكيد"
        cancelText="إلغاء"
        confirmLoading={loading}
        onCancel={() => setVisible(false)}
        onOk={completeOtp}
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <Typography.Paragraph>أدخل رمز التحقق المرسل إلى {phone}.</Typography.Paragraph>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="رمز التحقق" maxLength={6} />
          {!userExists && (
            <>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="الاسم الكامل" />
              <Input.TextArea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} placeholder="العنوان الكامل" />
              <div style={{ display: 'grid', gap: 4 }}>
                <Typography.Text>منطقة التوصيل</Typography.Text>
                <Select
                  value={deliveryLocationId ?? undefined}
                  onChange={(val) => setDeliveryLocationId(val)}
                  loading={locLoading}
                  placeholder="اختر منطقة التوصيل"
                  options={(locations || []).map(l => ({ value: l.id, label: `${l.id} - ${(l.nameAr || l.name || '')}` }))}
                />
                <Typography.Text type="secondary">إن لم تكن محددة مسبقاً، الرجاء اختيار منطقتك.</Typography.Text>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
})
